<template>
    <div class="email-ai-draft-controls -mt-px" @click.stop>
        <div :class="{ 'border-b': !showUpdatePanel, 'border-gray-300': !showUpdatePanel }">
            <div
                class="px-3 py-2.5 flex flex-col gap-2"
                data-testid="email-ai-update-panel"
                v-if="showUpdatePanel"
            >
                <div class="flex items-center gap-3">
                    <div
                        class="min-h-[42px] grow rounded-lg border bg-white shadow-sm"
                        data-testid="email-ai-update-input-shell"
                        :class="updateInputShellClasses"
                    >
                        <div
                            class="relative z-10 flex min-h-[40px] items-center gap-2 rounded-[7px] bg-white px-2.5 py-[8px]"
                        >
                            <icon
                                class="h-[18px] w-[18px] shrink-0"
                                data-testid="email-ai-update-icon"
                                name="regular/ai-edit-spark"
                                style="color: #0000ff"
                            />
                            <textarea
                                id="email-ai-update-instruction"
                                class="email-ai-update-instruction min-h-[20px] max-h-[76px] grow resize-none overflow-hidden border-0 bg-transparent p-0 text-sm text-gray-900 outline-none placeholder:text-gray-550 disabled:cursor-not-allowed disabled:text-gray-500"
                                data-testid="email-ai-update-instruction-input"
                                maxlength="255"
                                rows="1"
                                :aria-label="$t('mailClient.aiDraft.updatePlaceholder')"
                                :disabled="isInProgress || isRateLimited"
                                :placeholder="$t('mailClient.aiDraft.updatePlaceholder')"
                                ref="updateInstructionInput"
                                @blur="instructionIsFocused = false"
                                @focus="instructionIsFocused = true"
                                @input="resizeInstructionInput"
                                @keydown.enter.prevent="updateDraft"
                                v-model="instruction"
                            />
                            <activix-tooltip
                                data-testid="email-ai-update-submit-tooltip"
                                :content="updateDisabledReason ? $t(updateDisabledReason) : ''"
                                :disabled="!updateDisabledReason"
                                v-if="hasInstruction"
                            >
                                <span class="inline-flex shrink-0">
                                    <ai-draft-submit-button
                                        :disabled="!canSubmitUpdate"
                                        :label="$t('mailClient.aiDraft.updateWithAi')"
                                        testid-prefix="email-ai"
                                        @click="updateDraft"
                                    />
                                </span>
                            </activix-tooltip>
                        </div>
                    </div>

                    <activix-tooltip
                        data-testid="email-ai-undo-tooltip"
                        :content="$t('mailClient.aiDraft.undoTooltip')"
                    >
                        <span class="inline-flex shrink-0">
                            <button
                                class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                data-testid="email-ai-undo-button"
                                type="button"
                                :aria-label="$t('mailClient.aiDraft.undo')"
                                :disabled="!canUseUndo"
                                @click="undoDraft"
                            >
                                <icon
                                    class="h-[15px] w-[15px]"
                                    data-testid="email-ai-undo-icon"
                                    name="regular/undo"
                                />
                                <span class="sr-only">{{ $t('mailClient.aiDraft.undo') }}</span>
                            </button>
                        </span>
                    </activix-tooltip>
                </div>

                <ai-draft-unsupported-alert
                    class="sm:max-w-[592px]"
                    close-control="email"
                    data-testid="email-ai-unsupported-alert"
                    :dismiss-label="$t('mailClient.aiDraft.unsupportedDismiss')"
                    :message="$t('mailClient.aiDraft.unsupportedUpdate')"
                    @dismiss="unsupportedDismissed = true"
                    v-if="showUnsupportedAlert"
                />
            </div>

            <div
                class="min-h-[54px] px-3 py-2.5 flex flex-col justify-between gap-2"
                data-testid="email-ai-action-row"
                v-else-if="showActionRow"
            >
                <div class="flex flex-col gap-2 | sm:flex-row sm:items-center">
                    <button
                        class="email-ai-action-button inline-flex h-[34px] items-center justify-center gap-1 rounded px-3 py-0.5 text-sm font-normal disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid="email-ai-generate-button"
                        type="button"
                        :disabled="isInProgress || isRateLimited || !canGenerate"
                        @click="generateDraft"
                        v-if="showGenerateButton"
                    >
                        <icon data-testid="email-ai-generate-icon" name="regular/ai-edit-spark" />
                        <span data-testid="email-ai-generate-label">
                            {{ $t('mailClient.aiDraft.generateDraft') }}
                        </span>
                    </button>

                    <span
                        class="text-sm text-gray-400"
                        data-testid="email-ai-service-lead-helper"
                        v-if="serviceLeadDisabled && !isInProgress"
                    >
                        {{ $t('mailClient.aiDraft.serviceLeadUnavailable') }}
                    </span>
                </div>

                <ai-draft-unsupported-alert
                    class="sm:max-w-[592px]"
                    close-control="email"
                    data-testid="email-ai-unsupported-alert"
                    :dismiss-label="$t('mailClient.aiDraft.unsupportedDismiss')"
                    :message="$t('mailClient.aiDraft.unsupportedUpdate')"
                    @dismiss="unsupportedDismissed = true"
                    v-if="showUnsupportedAlert"
                />
            </div>

            <div
                class="px-3 pb-2 flex flex-col items-start gap-2"
                v-if="state === EmailAiDraftState.Failed || state === EmailAiDraftState.RateLimited"
            >
                <div
                    class="inline-flex max-w-full items-center gap-2 text-sm text-gray-700"
                    data-testid="email-ai-error-alert"
                    v-if="state === EmailAiDraftState.Failed"
                >
                    <icon class="h-4 w-4 shrink-0 text-red-600" name="bold/alert-circle" />
                    <span>
                        {{ $t('mailClient.aiDraft.error') }}
                        <a class="text-blue-500" href="mailto:support@activix.ca"
                            >support@activix.ca</a
                        >.
                    </span>
                </div>

                <div
                    class="inline-flex max-w-full items-center gap-2 text-sm text-gray-700"
                    data-testid="email-ai-rate-limit-alert"
                    v-if="state === EmailAiDraftState.RateLimited"
                >
                    <icon class="h-4 w-4 shrink-0 text-orange-600" name="bold/alert-circle" />
                    <span>{{
                        $t('mailClient.aiDraft.rateLimit', { seconds: rateLimitSeconds })
                    }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, nextTick, ref, watch } from 'vue';
    import AiDraftUnsupportedAlert from '@/components/aiDraft/AiDraftUnsupportedAlert.vue';
    import AiDraftSubmitButton from '@/components/aiDraft/AiDraftSubmitButton.vue';
    import { EmailAiDraftState } from '@/components/lead_xpress/aiDraft/useEmailAiDraft.ts';

    const props = defineProps<{
        canGenerate: boolean;
        canUndo: boolean;
        canUpdate: boolean;
        hasGeneratedDraft: boolean;
        rateLimitSeconds: number;
        serviceLeadDisabled: boolean;
        state: EmailAiDraftState;
        updateDisabledReason?: string | null;
    }>();

    const emit = defineEmits<{
        generate: [];
        undo: [];
        update: [payload: { instruction: string }];
    }>();

    const instruction = ref('');
    const instructionIsFocused = ref(false);
    const unsupportedDismissed = ref(false);
    const updateInstructionInput = ref<HTMLTextAreaElement | null>(null);

    const isInProgress = computed<boolean>(() => props.state === EmailAiDraftState.Loading);
    const isRateLimited = computed<boolean>(() => props.state === EmailAiDraftState.RateLimited);
    const isGenerated = computed<boolean>(() => {
        const stateHasGeneratedDraft =
            props.state === EmailAiDraftState.Inserted ||
            props.state === EmailAiDraftState.UpdateUnsupported;

        return props.hasGeneratedDraft && stateHasGeneratedDraft;
    });
    const isUnsupported = computed<boolean>(
        () => props.state === EmailAiDraftState.UpdateUnsupported
    );
    const showUnsupportedAlert = computed<boolean>(
        () => isUnsupported.value && !unsupportedDismissed.value
    );
    const trimmedInstruction = computed<string>(() => instruction.value.trim());
    const hasInstruction = computed<boolean>(() => !!trimmedInstruction.value);
    const canSubmitUpdate = computed<boolean>(
        () =>
            props.canUpdate &&
            !props.updateDisabledReason &&
            hasInstruction.value &&
            !isInProgress.value &&
            !isRateLimited.value
    );
    const canUseUndo = computed<boolean>(() => props.canUndo && !isInProgress.value);
    const showUpdatePanel = computed<boolean>(
        () => (props.canUpdate || !!props.updateDisabledReason) && !props.serviceLeadDisabled
    );
    const showGenerateButton = computed<boolean>(
        () =>
            !showUpdatePanel.value &&
            !isGenerated.value &&
            (props.canGenerate ||
                props.serviceLeadDisabled ||
                isInProgress.value ||
                props.state === EmailAiDraftState.Failed ||
                props.state === EmailAiDraftState.RateLimited)
    );
    const showActionRow = computed<boolean>(
        () => showGenerateButton.value || props.serviceLeadDisabled
    );
    const updateInputShellClasses = computed<string>(() =>
        instructionIsFocused.value || hasInstruction.value
            ? 'email-ai-gradient-border'
            : 'border-gray-300'
    );

    const resizeInstructionInput = () => {
        const input = updateInstructionInput.value;

        if (!input) {
            return;
        }

        input.style.height = 'auto';
        input.style.height = `${Math.min(Math.max(input.scrollHeight, 20), 76)}px`;
    };

    watch(instruction, () => {
        nextTick(resizeInstructionInput);
    });

    watch(
        () => props.state,
        (nextState) => {
            if (nextState === EmailAiDraftState.Loading) {
                unsupportedDismissed.value = false;
            }
        }
    );

    const generateDraft = () => {
        emit('generate');
    };

    const updateDraft = () => {
        if (!canSubmitUpdate.value) {
            return;
        }

        emit('update', { instruction: trimmedInstruction.value });
    };

    const undoDraft = () => {
        if (!canUseUndo.value) {
            return;
        }

        emit('undo');
    };
</script>

<style scoped>
    .email-ai-action-button {
        color: #0038ff;
        border: 1px solid transparent;
        background: linear-gradient(#fff, #fff) padding-box,
            linear-gradient(105deg, #0038ff 0%, #0038ff 45%, #ff2d55 100%) border-box;
    }

    .email-ai-gradient-border {
        position: relative;
        background: #fff;
    }

    .email-ai-gradient-border::before {
        position: absolute;
        inset: -1px;
        content: '';
        pointer-events: none;
        border-radius: inherit;
        background: linear-gradient(105deg, #0038ff 0%, #0038ff 42%, #fe3031 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask-composite: exclude;
        padding: 1px;
    }

    .email-ai-update-instruction {
        line-height: 20px;
    }
</style>
